import { FC } from 'react'
import {
    AlertsContainer,
    ChartContainer,
    AlertsList,
    AlertItem,
    AlertDot,
    AlertValue,
    AlertLabel,
    SectionHeader,
    SectionTitle,
    SectionHeaderContent,
} from '../../overview.styles'
import { CenterLabel, Container, StyledCard } from './AlertsOverview.styles'
import { useCustomTheme } from '@/core/theme'
import { useAlertsOverview } from './useAlertsOverview'
import { PieChart } from '@/ui/components/PieChart'
import InfoIconSvg from '@assets/images/info-icon.svg?react'
import { useRouting } from '@/core/hooks/useRouting'
import { MdrModule } from '@/core/modules/mdr/mdr.module'
import { CustomTooltip } from '@atbay/component-lib'
import { AccountModule } from '@/core/modules/account/account.module'

export const AlertsOverview: FC = () => {
    const theme: any = useCustomTheme()
    const { alertsConfig, pieDataToShow, total, isEmpty, emptyPieBorderColor } =
        useAlertsOverview()
    const { ROUTES, navigateTo } = useRouting()
    const { isMdrActive } = AccountModule.useStore()

    const labelToIdMap = Object.fromEntries(
        alertsConfig.map((a) => [a.label, a.id])
    )

    const handleSeverityClick = (label: string) => {
        const severity = labelToIdMap[label] as TIssueSeverity
        if (!severity) return

        MdrModule.getFilterModuleForTab('alerts').setFilter('severity', [
            severity,
        ])
        navigateTo({
            path: ROUTES.mdr.children.alerts.path,
            event: {
                name: 'SEM::alerts_filtered_by_severity',
                params: {
                    origin: 'mdr_alerts_overview',
                    severity,
                    isMdrActive,
                },
            },
        })
    }

    return (
        <Container>
            <SectionHeader>
                <SectionHeaderContent>
                    <SectionTitle variant="m-semibold" component="h2">
                        Alerts Overview
                    </SectionTitle>
                    <CustomTooltip
                        title="All security events or detections investigated or responded to by At-Bay MDR"
                        alwaysShowOnHover
                    >
                        <InfoIconSvg width={14} height={16} className="icon" />
                    </CustomTooltip>
                </SectionHeaderContent>
            </SectionHeader>
            <StyledCard>
                <AlertsContainer>
                    <ChartContainer>
                        <PieChart
                            data={pieDataToShow}
                            stroke={theme.colors.greys[75]}
                            strokeWidth={1}
                            emptyColor={emptyPieBorderColor}
                            isEmpty={isEmpty}
                            onSectionClick={handleSeverityClick}
                        />
                        <CenterLabel color={theme.colors.greys[800]}>
                            {total}
                        </CenterLabel>
                    </ChartContainer>
                    <AlertsList>
                        {alertsConfig.map((alert) => (
                            <AlertItem
                                key={alert.id}
                                data-testid={`alert-item-${alert.id}`}
                                onClick={() => handleSeverityClick(alert.label)}
                            >
                                <AlertDot
                                    severity={alert.id as TCaseSeverity}
                                />
                                <AlertValue
                                    data-testid={`alert-value-${alert.id}`}
                                >
                                    {alert.value}
                                </AlertValue>
                                <AlertLabel
                                    data-testid={`alert-label-${alert.id}`}
                                >
                                    {alert.label}
                                </AlertLabel>
                            </AlertItem>
                        ))}
                    </AlertsList>
                </AlertsContainer>
            </StyledCard>
        </Container>
    )
}
